// Read-side parser for the `auctions.documents` / `auction_lots.documents`
// JSONB arrays (BL-1476).
//
// Two jobs, both previously done in two byte-identical private copies of
// `normalizeExternalDocuments` — one in `rings-lot-mapper.ts`, one in the
// auction lot route's helper module (since deleted, BL-1833 PR 3b-1)
// (`sharing.md` § 1/§ 4):
//
//   1. Normalize the locator. Historical rows persisted snake_case
//      `storage_path`; the client contract is camelCase `storagePath`, and
//      without the normalization client-side deletion misses the storage
//      cleanup and orphans the file in the bucket.
//   2. **Resolve the servable URL, preferring the path** — the reason this
//      module exists. The GCS cutover (BL-1459) left every document row
//      carrying BOTH a `storagePath` and the absolute Supabase URL it was
//      derived from, and the read paths were emitting the stored URL verbatim.
//      Documents therefore kept serving from the store being decommissioned,
//      silently, while media / hero images / logos had already moved.
//
// Pure and client-safe — no `server-only`, no I/O, no gateway import. The
// builder is INJECTED (`resolveStoredFileUrl`'s contract) so server read paths
// hand in `getAuctionDocumentsUrlBuilder()` while client call sites, which only
// ever receive already-resolved arrays, pass nothing.
//
// **The builder is REQUIRED (BL-1477).** It used to be optional, and an
// unwired call site then degraded silently: every document row carried a `url`,
// so the list kept rendering while quietly serving the legacy address. That is
// invisible in the UI, which is why each server wiring site also carries a test
// asserting the BUILT address rather than merely a non-empty list. Making the
// parameter mandatory turns that whole class into a compile error.
//
// NOT the bidder projection: `@auctogon/core-bidder`'s `parseExternalDocuments`
// produces a deliberate snake_case (`storage_path`) API-boundary re-projection
// of the same column for the public site. It applies the same precedence via
// the same resolver, but its output shape is intentionally different and is not
// consolidated here — see the note on `ExternalDocument` in `@auctogon/db`.
import type { ResolvedExternalDocument } from '@auctogon/db/types/jsonb';

import { resolveStoredFileUrl, type StoredFileUrlBuilder } from '../storage/stored-file-url';

/**
 * Maximum number of external documents per lot — a generous guard against
 * runaway payloads. One source for the whole-array validator and the per-item
 * add (BL-2338), so the two doors cannot disagree on when a lot is full.
 */
export const MAX_EXTERNAL_DOCS_PER_LOT = 50;

/**
 * BUG-2061 — the longest document id and name a lot or auction stores. Documents
 * are bidder-visible JSONB, so an unbounded field bloats the row and the public
 * catalog payload. The id matches the media id cap; the name matches the staged
 * document ref's and the auction Media page's.
 */
export const MAX_DOCUMENT_ID_LENGTH = 200;
export const MAX_DOCUMENT_NAME_LENGTH = 255;

/**
 * Parse a raw `documents` JSONB blob into {@link ResolvedExternalDocument}
 * records — the persisted shape with its `url` built from `storagePath`.
 *
 * Resolution is {@link resolveStoredFileUrl}'s: `storagePath` is the only
 * locator, and an entry without one is dropped. BL-1477 removed the fallback
 * to a persisted absolute `url` — an earlier version of this docstring called
 * it permanent, which its clear made false — so `buildUrl` is now REQUIRED.
 * That matters more here than elsewhere: while it was optional, a wiring site
 * that forgot it degraded silently to the stored Supabase address instead of
 * failing, which is the exact defect BL-1476 existed to fix. It is now a
 * compile error.
 *
 * Malformed (null / non-object) legacy elements are skipped rather than thrown
 * on: a stray element in a historical blob must not turn a lot's GET into an
 * unhandled 500.
 *
 * @param raw      the JSONB value (unknown shape)
 * @param buildUrl server-side builder — `getAuctionDocumentsUrlBuilder()` from
 *                 `@auctogon/gateways/storage`
 * @returns typed records in stored order, each with a servable `url`
 * @sideEffect none — pure
 */
export function parseExternalDocuments(
  raw: unknown,
  buildUrl: StoredFileUrlBuilder,
): ResolvedExternalDocument[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry): ResolvedExternalDocument[] => {
    if (!entry || typeof entry !== 'object') return [];
    // eslint-disable-next-line responsecast/no-response-type-erasure -- JSONB narrow: widening a genuinely `unknown` array element to probe the canonical ExternalDocument fields; not erasing a typed DTO.
    const d = entry as Record<string, unknown>;
    // Narrowed by TYPE, not by `??` alone: both copies this replaces wrote
    // `(d.storagePath ?? d.storage_path) as string | undefined`, which yields a
    // literal `null` when both fields are explicitly null and then leaks it
    // through the `!== undefined` output guard into a field typed
    // `string | undefined`. Checking `typeof` collapses null, undefined, and a
    // non-string legacy value to the same absent case.
    const rawPath = d.storagePath ?? d.storage_path;
    const storagePath = typeof rawPath === 'string' ? rawPath : undefined;
    const url = resolveStoredFileUrl(storagePath, buildUrl);
    // An entry with no resolvable path has nothing to link to. The two admin
    // copies this replaces emitted `url: undefined` and rendered a dead anchor;
    // dropping it is the honest reading of the same row.
    //
    // BL-1477 also retired the stored-XSS guard that sat here. It fired only
    // when the value being served WAS the stored one — `url === storedUrl` —
    // to keep a `javascript:` / `data:` value in a legacy `url` out of the
    // admin document list's `<a href>`. With the legacy branch gone, every url
    // this returns is one we built ourselves from a `storagePathSchema`-
    // validated key, so the condition can no longer be true and the guard was
    // unreachable. The input it defended against is what the migration cleared.
    if (url == null) return [];
    return [
      {
        id: d.id as string,
        name: d.name as string,
        url,
        type: d.type as ResolvedExternalDocument['type'],
        uploadedAt: d.uploadedAt as string,
        ...(storagePath !== undefined && storagePath !== '' ? { storagePath } : {}),
        // BL-961's auction→lot fan-out flag. Carried only when the row states
        // it: absent means `true` at the point of use, and materializing that
        // default here would make an unset flag indistinguishable from an
        // explicit opt-in on write-back.
        ...(typeof d.appliesToAllLots === 'boolean'
          ? { appliesToAllLots: d.appliesToAllLots }
          : {}),
      },
    ];
  });
}
